import { useState } from "react";
import { API, apiPost, apiPut, apiDelete } from "../lib/api";
import { MONTH_NAMES } from "../lib/dates";
import { BRAND } from "../lib/brand";
import { SartellLogo, SmallLogo } from "../components/Logo";
import { ShareLinksPanel } from "../components/ShareLinksPanel";
import { TeamRosterUpload } from "../components/TeamRosterUpload";
import { RosterPanel } from "../components/RosterPanel";
import { FamilyInvitePanel } from "../components/FamilyInvitePanel";
import { SectionHelp } from "../components/SectionHelp";
import { TeamBadge } from "../components/TeamBadge";
import { ColorPicker } from "../components/ColorPicker";

/* ───────────────────── ADMIN PANEL ───────────────────── */
export function AdminPanel({ onBack }) {
  const [adminCode, setAdminCode] = useState("");
  const [authed, setAuthed] = useState(false);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [teamName, setTeamName] = useState("");
  const [parentPasscode, setParentPasscode] = useState("");
  const [coachPasscode, setCoachPasscode] = useState("");
  const [ageGroup, setAgeGroup] = useState("");
  const [season, setSeason] = useState("Spring 2025");
  const now = new Date();
  const [seasonStartMonth, setSeasonStartMonth] = useState(now.getMonth());
  const [seasonStartYear, setSeasonStartYear] = useState(now.getFullYear());
  const [seasonEndMonth, setSeasonEndMonth] = useState((now.getMonth() + 2) % 12);
  const [seasonEndYear, setSeasonEndYear] = useState(now.getMonth() + 2 > 11 ? now.getFullYear() + 1 : now.getFullYear());

  const [editingTeam, setEditingTeam] = useState(null);
  const [editPasscode, setEditPasscode] = useState("");
  const [editCoachPasscode, setEditCoachPasscode] = useState("");
  const [editSeason, setEditSeason] = useState("");
  const [editAgeGroup, setEditAgeGroup] = useState("");
  const [teamColor, setTeamColor] = useState("");
  const [editColor, setEditColor] = useState("");
  const [editStartMonth, setEditStartMonth] = useState(0);
  const [editStartYear, setEditStartYear] = useState(2025);
  const [editEndMonth, setEditEndMonth] = useState(2);
  const [editEndYear, setEditEndYear] = useState(2025);
  const [showPasscodes, setShowPasscodes] = useState({});
  const [showShareLinks, setShowShareLinks] = useState({});
  const [showBulk, setShowBulk] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [bulkText, setBulkText] = useState("");
  const [bulkProgress, setBulkProgress] = useState("");
  const [showRoster, setShowRoster] = useState({});

  const loadTeams = async (code) => {
    try {
      const headers = {};
      if (code || adminCode) headers["x-admin-code"] = code || adminCode;
      const res = await fetch(API + "/teams", { headers });
      const data = await res.json();
      setTeams(data.teams || []);
    } catch (e) {
      setError(e.message);
    }
  };

  const handleAdminLogin = async () => {
    if (!adminCode.trim()) return;
    setLoading(true);
    setError("");
    try {
      await loadTeams(adminCode.trim());
      setAuthed(true);
    } catch (e) {
      setError("Failed to connect");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTeam = async () => {
    if (!teamName.trim() || !parentPasscode.trim()) {
      setError("Team name and parent passcode are required");
      return;
    }
    setError("");
    setSuccess("");
    setLoading(true);
    try {
      await apiPost("/teams-manage", {
        name: teamName.trim(),
        passcode: parentPasscode.trim(),
        coachPasscode: coachPasscode.trim() || parentPasscode.trim() + "-coach",
        ageGroup: ageGroup.trim(),
        color: teamColor,
        season: season.trim(),
        seasonStartMonth,
        seasonStartYear,
        seasonEndMonth,
        seasonEndYear,
      }, null, adminCode);
      setSuccess("Team '" + teamName.trim() + "' created!");
      setTeamName("");
      setParentPasscode("");
      setCoachPasscode("");
      setAgeGroup("");
      setTeamColor("");
      await loadTeams();
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleBulkCreate = async () => {
    const lines = bulkText.trim().split("\n").filter((l) => l.trim());
    if (lines.length === 0) { setError("No teams to create"); return; }
    setError("");
    setSuccess("");
    setBulkProgress("Starting...");
    let created = 0;
    let failed = 0;
    for (let i = 0; i < lines.length; i++) {
      const parts = lines[i].split(",").map((s) => s.trim());
      const name = parts[0];
      const ageGroup = parts[1] || "";
      const parentPass = parts[2] || name.toLowerCase().replace(/[^a-z0-9]+/g, "-");
      const coachPass = parts[3] || parentPass + "-coach";
      if (!name) { failed++; continue; }
      setBulkProgress("Creating " + (i + 1) + " of " + lines.length + ": " + name);
      try {
        await apiPost("/teams-manage", {
          name,
          passcode: parentPass,
          coachPasscode: coachPass,
          ageGroup,
          season: season.trim(),
          seasonStartMonth,
          seasonStartYear,
          seasonEndMonth,
          seasonEndYear,
        }, null, adminCode);
        created++;
      } catch (e) {
        failed++;
      }
    }
    setBulkProgress("");
    setSuccess(created + " team" + (created !== 1 ? "s" : "") + " created" + (failed > 0 ? ", " + failed + " failed" : ""));
    setBulkText("");
    setShowBulk(false);
    await loadTeams();
  };

  const handleStartEdit = (team) => {
    setEditingTeam(team.id);
    setEditPasscode(team.passcode || "");
    setEditCoachPasscode(team.coachPasscode || "");
    setEditSeason(team.season || "");
    setEditAgeGroup(team.ageGroup || "");
    setEditColor(team.color || "");
    setEditStartMonth(team.seasonStartMonth !== undefined && team.seasonStartMonth !== -1 ? team.seasonStartMonth : now.getMonth());
    setEditStartYear(team.seasonStartYear || now.getFullYear());
    setEditEndMonth(team.seasonEndMonth !== undefined && team.seasonEndMonth !== -1 ? team.seasonEndMonth : (now.getMonth() + 2) % 12);
    setEditEndYear(team.seasonEndYear || now.getFullYear());
  };

  const handleSaveTeamEdit = async (teamId) => {
    setError("");
    setSuccess("");
    try {
      await apiPut("/teams-manage", {
        teamId,
        passcode: editPasscode.trim(),
        coachPasscode: editCoachPasscode.trim(),
        season: editSeason.trim(),
        ageGroup: editAgeGroup.trim(),
        color: editColor,
        seasonStartMonth: editStartMonth,
        seasonStartYear: editStartYear,
        seasonEndMonth: editEndMonth,
        seasonEndYear: editEndYear,
      }, adminCode);
      setSuccess("Team updated!");
      setEditingTeam(null);
      await loadTeams();
    } catch (e) {
      setError(e.message);
    }
  };

  const toggleShowPasscode = (teamId) => {
    setShowPasscodes((prev) => ({ ...prev, [teamId]: !prev[teamId] }));
  };

  const handleClearData = async (teamId, tName) => {
    if (!window.confirm("Clear all player availability data for " + tName + "? Players will need to re-submit.")) return;
    setError("");
    setSuccess("");
    try {
      await apiPut("/teams-manage", { teamId, clearAvailability: true }, adminCode);
      setSuccess("Availability data cleared for '" + tName + "'");
    } catch (e) {
      setError(e.message);
    }
  };

  const toggleRoster = (teamId) => {
    setShowRoster((p) => ({ ...p, [teamId]: !p[teamId] }));
  };

  const handleDeleteTeam = async (teamId, tName) => {
    if (!window.confirm("Delete " + tName + "? This also removes all player availability data.")) return;
    setError("");
    setSuccess("");
    try {
      await apiDelete("/teams-manage?teamId=" + teamId, adminCode);
      setSuccess("Team '" + tName + "' deleted");
      setEditingTeam(null);
      await loadTeams();
    } catch (e) {
      setError(e.message);
    }
  };

  if (!authed) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-sky-50 to-white flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="mb-8"><SartellLogo size="lg" /></div>
          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
            <button onClick={onBack} className="text-sm hover:text-blue-600 mb-4 flex items-center gap-1" style={{ color: BRAND.accent }}>
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Back
            </button>
            <h2 className="text-xl font-bold text-gray-800 text-center mb-1">Admin Panel</h2>
            <p className="text-sm text-gray-500 text-center mb-6">Enter admin passcode to manage teams</p>
            <input
              type="password"
              placeholder="Admin passcode..."
              value={adminCode}
              onChange={(e) => setAdminCode(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleAdminLogin()}
              className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent mb-4"
              autoFocus
            />
            {error && <p className="text-sm text-red-500 text-center mb-3">{error}</p>}
            <button
              onClick={handleAdminLogin}
              disabled={loading}
              className="w-full py-3 text-white font-semibold rounded-xl transition-colors shadow-sm disabled:opacity-50"
              style={{ backgroundColor: BRAND.primary }}
            >
              {loading ? "Connecting..." : "Login"}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-sky-50 to-white p-4 md:p-8">
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <SmallLogo />
            <div>
              <h1 className="text-xl font-extrabold leading-none" style={{ color: BRAND.accent }}>Sartell Soccer</h1>
              <p className="text-xs font-medium" style={{ color: BRAND.primary }}>Admin Panel</p>
            </div>
          </div>
          <button onClick={onBack} className="px-4 py-2 text-sm text-gray-600 border border-gray-200 rounded-xl hover:bg-gray-50">
            Back to App
          </button>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl p-3 mb-4">{error}</div>
        )}
        {success && (
          <div className="bg-green-50 border border-green-200 text-green-700 text-sm rounded-xl p-3 mb-4">{success}</div>
        )}

        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 mb-6">
          <h3 className="text-lg font-bold text-gray-800 mb-1">Create New Team</h3>
          <SectionHelp>
            <p>
              <strong>One at a time</strong> — fill this in and hit Create Team. Only the name
              is required; passcodes generate themselves if you leave them blank.
            </p>
            <p>
              <strong>Bulk Create</strong> — paste one team per line when you're setting up a
              whole age group. Teams only, no players.
            </p>
            <p>
              <strong>Upload CSV</strong> — the usual start-of-season route. One row per
              player, and it creates the teams AND fills their rosters in one go. Add a
              <strong> Parent Email</strong> column and you can email families their own link
              instead of handing out shared passcodes. Templates are on the upload screen.
            </p>
            <p>
              The <strong>Season Date Range</strong> above sets which months parents can mark,
              and applies to every team you create here.
            </p>
          </SectionHelp>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Team Name *</label>
              <input type="text" placeholder="e.g. Sartell U10 Blue" value={teamName}
                onChange={(e) => setTeamName(e.target.value)}
                className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Age Group</label>
              <input type="text" placeholder="e.g. U10" value={ageGroup}
                onChange={(e) => setAgeGroup(e.target.value)}
                className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Parent Passcode *</label>
              <input type="text" placeholder="e.g. sartell-u10b" value={parentPasscode}
                onChange={(e) => setParentPasscode(e.target.value)}
                className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Coach Passcode</label>
              <input type="text" placeholder="Auto: parent code + '-coach'" value={coachPasscode}
                onChange={(e) => setCoachPasscode(e.target.value)}
                className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Season Label</label>
              <input type="text" placeholder="Spring 2025" value={season}
                onChange={(e) => setSeason(e.target.value)}
                className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Badge Color</label>
              <ColorPicker value={teamColor} onChange={setTeamColor} teamName={teamName} />
            </div>
          </div>

          <div className="mb-4">
            <label className="block text-xs font-medium text-gray-500 mb-2">Season Date Range</label>
            <div className="flex items-center gap-2 flex-wrap">
              <select value={seasonStartMonth} onChange={(e) => setSeasonStartMonth(parseInt(e.target.value))}
                className="px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400">
                {MONTH_NAMES.map((n, i) => <option key={i} value={i}>{n}</option>)}
              </select>
              <select value={seasonStartYear} onChange={(e) => setSeasonStartYear(parseInt(e.target.value))}
                className="px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400">
                {[2025, 2026, 2027, 2028].map((y) => <option key={y} value={y}>{y}</option>)}
              </select>
              <span className="text-sm text-gray-400 font-medium">to</span>
              <select value={seasonEndMonth} onChange={(e) => setSeasonEndMonth(parseInt(e.target.value))}
                className="px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400">
                {MONTH_NAMES.map((n, i) => <option key={i} value={i}>{n}</option>)}
              </select>
              <select value={seasonEndYear} onChange={(e) => setSeasonEndYear(parseInt(e.target.value))}
                className="px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400">
                {[2025, 2026, 2027, 2028].map((y) => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={handleCreateTeam}
              disabled={loading || !teamName.trim() || !parentPasscode.trim()}
              className="px-6 py-2.5 text-white text-sm font-semibold rounded-xl transition-colors shadow-sm disabled:opacity-50"
              style={{ backgroundColor: BRAND.primary }}
            >
              {loading ? "Creating..." : "Create Team"}
            </button>
            <button
              onClick={() => setShowBulk(!showBulk)}
              className="px-4 py-2.5 text-sm font-medium text-gray-600 border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors"
            >
              {showBulk ? "Hide Bulk" : "Bulk Create"}
            </button>
            <button
              onClick={() => setShowUpload(!showUpload)}
              className="px-4 py-2.5 text-sm font-medium text-gray-600 border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors"
            >
              {showUpload ? "Hide Upload" : "Upload CSV"}
            </button>
          </div>

          {showBulk && (
            <div className="mt-4 pt-4 border-t border-gray-100">
              <h4 className="text-sm font-semibold text-gray-700 mb-2">Bulk Create Teams</h4>
              <p className="text-xs text-gray-500 mb-2">
                One team per line. Format: <code className="bg-gray-100 px-1 rounded">Team Name, Age Group, Parent Passcode, Coach Passcode</code><br />
                Only name is required. Passcodes auto-generate if omitted. Season dates above apply to all.
              </p>
              <textarea
                value={bulkText}
                onChange={(e) => setBulkText(e.target.value)}
                placeholder={"Sartell U8 Blue, U8, sartell-u8b\nSartell U8 White, U8, sartell-u8w\nSartell U10 Blue, U10\nSartell U10 White, U10\nSartell U12 Blue, U12"}
                className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-400 min-h-[120px] mb-3"
              />
              {bulkProgress && (
                <p className="text-sm text-blue-600 mb-2 animate-pulse">{bulkProgress}</p>
              )}
              <button
                onClick={handleBulkCreate}
                disabled={!bulkText.trim() || !!bulkProgress}
                className="px-6 py-2 text-white text-sm font-semibold rounded-xl transition-colors shadow-sm disabled:opacity-50"
                style={{ backgroundColor: "#16a34a" }}
              >
                {bulkProgress ? "Creating..." : "Create All Teams"}
              </button>
            </div>
          )}

          {showUpload && (
            <TeamRosterUpload
              adminCode={adminCode}
              season={season}
              seasonStartMonth={seasonStartMonth}
              seasonStartYear={seasonStartYear}
              seasonEndMonth={seasonEndMonth}
              seasonEndYear={seasonEndYear}
              onTeamsChanged={loadTeams}
              onClose={() => setShowUpload(false)}
            />
          )}
        </div>

        <div className="mb-5">
          <FamilyInvitePanel adminCode={adminCode} />
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
          <h3 className="text-lg font-bold text-gray-800 mb-1">Existing Teams ({teams.length})</h3>
          <SectionHelp label="What do these buttons do?">
            <p>
              <strong>Share</strong> — the team's parent and coach links, as a link to paste
              and a QR code to print or show at practice. Parent and coach codes are
              different: the coach one unlocks the whole-team view.
            </p>
            <p>
              <strong>Edit</strong> — rename, change passcodes, age group, color or calendar
              range. Changing a passcode immediately stops the old links and QR codes working.
            </p>
            <p>
              <strong>Roster</strong> — add, rename or remove players. Renaming keeps that
              player's availability, their parent email and their jersey number.
            </p>
            <p>
              <strong>Coaches &amp; staff</strong> — add them with the blue button, or <strong>⇄</strong>
              to move someone between the two groups. They mark their own dates exactly like a
              family does, and get the same links and reminders. The coach view keeps them in
              their own section so a coach being away never counts against how many
              <em> players</em> can make a date.
            </p>
            <p>
              The <strong>✉</strong> on each player sets that family's email, at any time —
              you don't have to have it when the roster goes in, and you never need to
              re-upload the CSV to add one. Green means it's on file. Put the same email on
              every one of a family's players, including players on other teams, and they
              get one link covering all of them.
            </p>
            <p>
              <strong>Clear Data</strong> — wipes every family's submitted availability for
              this team but keeps the team and its roster. This is the season-rollover button.
              Everyone will need to fill theirs in again, and it can't be undone.
            </p>
            <p>
              <strong>Delete</strong> — removes the team, its roster and its availability
              entirely. Permanent.
            </p>
          </SectionHelp>

          {teams.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-6">No teams yet. Create one above.</p>
          ) : (
            <div className="space-y-3">
              {teams.map((t) => (
                <div key={t.id} className="p-4 rounded-xl border border-gray-200 hover:border-gray-300 transition-colors">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <TeamBadge team={t} size="md" />
                      <div>
                        <div className="font-semibold text-gray-800">{t.name}</div>
                        <div className="text-xs text-gray-500">
                          {t.ageGroup}{t.ageGroup && " · "}{t.season}
                          {t.seasonStartMonth !== undefined && (
                            <span className="ml-1 text-gray-400">
                              ({MONTH_NAMES[t.seasonStartMonth].slice(0,3)} {t.seasonStartYear} – {MONTH_NAMES[t.seasonEndMonth].slice(0,3)} {t.seasonEndYear})
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button onClick={() => setShowShareLinks((p) => ({ ...p, [t.id]: !p[t.id] }))}
                        className="px-3 py-1.5 text-xs text-green-700 border border-green-200 rounded-lg hover:bg-green-50 transition-colors">
                        {showShareLinks[t.id] ? "Hide Links" : "Share"}
                      </button>
                      <button onClick={() => handleStartEdit(t)}
                        className="px-3 py-1.5 text-xs text-blue-700 border border-blue-200 rounded-lg hover:bg-blue-50 transition-colors">
                        {editingTeam === t.id ? "Editing..." : "Edit"}
                      </button>
                      <button onClick={() => toggleRoster(t.id)}
                        className="px-3 py-1.5 text-xs text-purple-700 border border-purple-200 rounded-lg hover:bg-purple-50 transition-colors">
                        {showRoster[t.id] ? "Hide Roster" : "Roster"}
                      </button>
                      <button onClick={() => handleClearData(t.id, t.name)}
                        className="px-3 py-1.5 text-xs text-orange-600 border border-orange-200 rounded-lg hover:bg-orange-50 transition-colors">
                        Clear Data
                      </button>
                      <button onClick={() => handleDeleteTeam(t.id, t.name)}
                        className="px-3 py-1.5 text-xs text-red-600 border border-red-200 rounded-lg hover:bg-red-50 transition-colors">
                        Delete
                      </button>
                    </div>
                  </div>

                  {showShareLinks[t.id] && (
                    <div className="mt-3 pt-3 border-t border-gray-100">
                      <ShareLinksPanel team={t} />
                    </div>
                  )}

                  {editingTeam === t.id && (
                    <div className="mt-3 pt-3 border-t border-gray-100 space-y-3">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        <div>
                          <label className="block text-xs text-gray-500 mb-1">Season Label</label>
                          <input type="text" value={editSeason} onChange={(e) => setEditSeason(e.target.value)}
                            className="w-full px-3 py-1.5 rounded-lg border border-gray-200 text-sm" />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-500 mb-1">Age Group</label>
                          <input type="text" value={editAgeGroup} onChange={(e) => setEditAgeGroup(e.target.value)}
                            className="w-full px-3 py-1.5 rounded-lg border border-gray-200 text-sm" />
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Badge Color</label>
                        <ColorPicker value={editColor} onChange={setEditColor} teamName={t.name} />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Calendar Range</label>
                        <div className="flex items-center gap-2 flex-wrap">
                          <select value={editStartMonth} onChange={(e) => setEditStartMonth(parseInt(e.target.value))}
                            className="px-2 py-1.5 rounded-lg border border-gray-200 text-sm">
                            {MONTH_NAMES.map((n, i) => <option key={i} value={i}>{n}</option>)}
                          </select>
                          <select value={editStartYear} onChange={(e) => setEditStartYear(parseInt(e.target.value))}
                            className="px-2 py-1.5 rounded-lg border border-gray-200 text-sm">
                            {[2025, 2026, 2027, 2028].map((y) => <option key={y} value={y}>{y}</option>)}
                          </select>
                          <span className="text-xs text-gray-400">to</span>
                          <select value={editEndMonth} onChange={(e) => setEditEndMonth(parseInt(e.target.value))}
                            className="px-2 py-1.5 rounded-lg border border-gray-200 text-sm">
                            {MONTH_NAMES.map((n, i) => <option key={i} value={i}>{n}</option>)}
                          </select>
                          <select value={editEndYear} onChange={(e) => setEditEndYear(parseInt(e.target.value))}
                            className="px-2 py-1.5 rounded-lg border border-gray-200 text-sm">
                            {[2025, 2026, 2027, 2028].map((y) => <option key={y} value={y}>{y}</option>)}
                          </select>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        <div>
                          <label className="block text-xs text-gray-500 mb-1">Parent Passcode</label>
                          <input type="text" value={editPasscode} onChange={(e) => setEditPasscode(e.target.value)}
                            className="w-full px-3 py-1.5 rounded-lg border border-gray-200 text-sm" />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-500 mb-1">Coach Passcode</label>
                          <input type="text" value={editCoachPasscode} onChange={(e) => setEditCoachPasscode(e.target.value)}
                            className="w-full px-3 py-1.5 rounded-lg border border-gray-200 text-sm" />
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <button onClick={() => handleSaveTeamEdit(t.id)}
                          className="px-4 py-1.5 text-xs text-white rounded-lg transition-colors" style={{ backgroundColor: BRAND.primary }}>Save Changes</button>
                        <button onClick={() => setEditingTeam(null)}
                          className="px-4 py-1.5 text-xs text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50">Cancel</button>
                      </div>
                    </div>
                  )}

                  {showRoster[t.id] && (
                    <div className="mt-3 pt-3 border-t border-gray-100">
                      <h4 className="text-sm font-semibold text-gray-700 mb-2">Roster</h4>
                      <RosterPanel
                        teamId={t.id}
                        auth={{ adminCode }}
                        allowRemove
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        <p className="text-center text-xs text-gray-400 mt-6">
          Share the parent passcode with families. Coach passcode gives the full team availability view.
        </p>
      </div>
    </div>
  );
}
